import React, { useState, useEffect } from "react";
import "firebase/firestore";
import { getProducts, createCheckoutSession, getCurrentUserSubscriptions } from "@stripe/firestore-stripe-payments";
import { httpsCallable } from "firebase/functions";
import dayjs from "dayjs";
import { Crown, Check, Star, ArrowRight } from "lucide-react";
import _ from "lodash";

import { payments, functions } from "../../firebase.js";
import Button from "../../components/Button.js";
import Message from "../../components/Message.js";
import VenueSelect from "../../components/VenueSelect.js";
import { planMeta, euroPerMonth } from "../../stripePlans.js";

const features = [
  "Uw advertentie valt meer op in de zoekresultaten",
  "Zichtbaarheid op sociale media en landingspagina's",
  "Bezoekers kunnen u direct contacteren",
  "Aanvragen rechtstreeks in uw email",
  "Vermelding in onze jaarlijkse magazine",
  "Regelmatig inhoudelijk onderhoud van uw pagina",
];

function PriceCard({ price, isPopular, onClick }) {
  const { title, period } = planMeta(price);
  const monthlyPrice = euroPerMonth(price, 0);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(e);
        }
      }}
      className={[
        "card relative p-6 cursor-pointer transition-all duration-normal hover:shadow-md hover:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 group",
        isPopular ? "border-accent ring-1 ring-accent" : "",
      ].join(" ")}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="badge bg-accent text-text-inverse px-3 py-1 text-xs font-semibold">Meest populair</span>
        </div>
      )}
      <div className="text-center">
        <p className="text-xs font-semibold uppercase tracking-wider text-accent mb-1">Premium</p>
        <h3 className="text-lg font-heading font-bold mb-4">{title}</h3>
        <div className="mb-4">
          <span className="text-3xl font-heading font-bold text-text-primary" data-tabular="true">
            &euro;{monthlyPrice}
          </span>
          <span className="text-sm text-text-secondary ml-1">/maand</span>
        </div>
        <p className="text-xs text-text-tertiary mb-5">{period} (excl. BTW)</p>
        <div className="flex items-center justify-center gap-2 text-sm font-medium text-accent group-hover:underline underline-offset-2">
          Selecteer <ArrowRight size={14} />
        </div>
      </div>
    </div>
  );
}

function Membership() {
  const [pending, setPending] = useState(false);
  const [venue, setVenue] = useState({});
  const [error, setError] = useState("");
  const [product, setProduct] = useState(false);
  const [subscription, setSubscription] = useState(false);
  const [pendingPortal, setPendingPortal] = useState(false);
  const [pendingSelect, setPendingSelect] = useState(false);

  const today = dayjs();

  async function toStripeCustomerPortal(event) {
    event.preventDefault();
    setPendingPortal(true);
    const funcRef = httpsCallable(functions, "ext-firestore-stripe-payments-createPortalLink");
    try {
      const resp = await funcRef({ returnUrl: window.location.href });
      window.location.assign(resp.data.url);
    } catch (err) {
      console.log(err);
      setError(err.message);
    }
    setPendingPortal(false);
  }

  async function onProductSelect(event, priceID) {
    event.preventDefault();
    setPendingSelect(true);
    try {
      const session = await createCheckoutSession(payments, {
        mode: "subscription",
        automatic_tax: true,
        allow_promotion_codes: true,
        price: priceID,
        success_url: window.location.href,
        cancel_url: window.location.href,
        metadata: { venue: venue.objectID },
      });
      window.location.assign(session.url);
    } catch (err) {
      console.log(err);
      setError(err.message);
    }
  }

  useEffect(() => {
    async function getData() {
      setPending(true);
      try {
        const subs = await getCurrentUserSubscriptions(payments, {
          status: ["active", "unpaid", "trialing"],
        });
        const sub = _.find(subs, (s) => s.metadata.venue === venue.objectID);
        setSubscription(sub);
      } catch (err) {
        console.log(err);
        setError(err.message);
      }

      try {
        const allProducts = await getProducts(payments, { includePrices: true, activeOnly: true });
        // Sluit het dienst-product uit; dit scherm is voor feestlocaties.
        const products = _.filter(allProducts, (p) => p.metadata?.type !== "service");
        if (!products.length) {
          setPending(false);
          return;
        }
        const prices = _.filter(products[0].prices, (price) => price.active);
        setProduct({ ...products[0], prices });
      } catch (err) {
        console.log(err);
        setError(err.message);
      }
      setPending(false);
    }
    getData();
  }, [venue.objectID]);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-subtle flex items-center justify-center">
          <Crown size={28} strokeWidth={1.5} className="text-accent" />
        </div>
        <h1 className="text-2xl font-heading font-bold mb-2">Word nu Premium lid!</h1>
        <p className="text-base text-text-secondary max-w-lg mx-auto">
          Een premium lidmaatschap biedt u alle voordelen en zorgt ervoor dat u extra goed in de spotlight staat.
        </p>
      </div>

      <div className="card p-6 mb-8">
        <h3 className="text-sm font-semibold text-text-primary mb-4">Premium voordelen</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {features.map((f, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <Check size={16} strokeWidth={2} className="text-success shrink-0 mt-0.5" />
              <span className="text-sm text-text-secondary">{f}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-8">
        <VenueSelect
          activeVenueId={venue.objectID}
          isStandAlone={true}
          preloadFirstItem={true}
          onChange={(_, venue) => {
            setSubscription(false);
            setProduct(false);
            setVenue(venue || {});
          }}
        />
      </div>

      {pendingSelect && (
        <div className="card p-6 text-center">
          <div className="w-6 h-6 mx-auto mb-3 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-text-secondary font-medium">U wordt doorgestuurd naar uw abonnement...</p>
        </div>
      )}

      {!pendingSelect && !pending && !subscription && venue.objectID && product && (
        <div className="mb-8">
          <h2 className="text-lg font-heading font-bold mb-4 text-center">Kies een abonnement</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {_.map(product.prices, (price) => (
              <PriceCard
                key={price.id}
                price={price}
                isPopular={planMeta(price).popular}
                onClick={(e) => onProductSelect(e, price.id)}
              />
            ))}
          </div>
        </div>
      )}

      {!subscription && !pending && venue.objectID && !pendingSelect && (
        <div className="card border-border bg-bg-sunken p-6">
          <div className="flex items-center gap-3 mb-2">
            <Star size={18} strokeWidth={1.5} className="text-text-tertiary" />
            <h3 className="text-base font-heading font-semibold">Freemium</h3>
          </div>
          <p className="text-sm text-text-secondary leading-relaxed">
            Dit is uw huidige abonnement en biedt een basisvermelding. Wilt u meer aanvragen en beter in de spotlight? Overweeg dan een van
            de bovenstaande opties.
          </p>
        </div>
      )}

      {!pending && venue.objectID && subscription && (
        <div className="card p-6">
          <h2 className="text-lg font-heading font-bold mb-3">Abonnement & facturen voor {venue.name}</h2>
          {!subscription.cancel_at_period_end && (
            <p className="text-sm text-text-secondary leading-relaxed mb-4">
              Uw abonnement is gestart op
              <strong>{dayjs(subscription.current_period_start).format(" D MMMM YYYY ")}</strong>
              en loopt tot
              <strong>{dayjs(subscription.current_period_end).format(" D MMMM YYYY ")}</strong>(
              {-today.diff(subscription.current_period_end, "days")} dagen over) en zal automatisch worden verlengd.
            </p>
          )}
          {subscription.cancel_at_period_end && (
            <p className="text-sm text-text-secondary leading-relaxed mb-4">
              Dit abonnement is geannuleerd. Het eindigt op
              <strong>{dayjs(subscription.current_period_end).format(" D MMMM YYYY")}</strong>.
            </p>
          )}
          {error && <Message text={error} />}
          <Button
            pending={pendingPortal}
            onClick={toStripeCustomerPortal}
            label="Abonnement wijzigen & facturen inzien"
            loadingLabel="Even geduld..."
            variant="secondary"
          />
        </div>
      )}
    </div>
  );
}

export default Membership;
